import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { useHandleInvitations } from '../hooks/useHandleInvitations';

import { Button } from '@/components';
import {
  TourneySearch,
  Sidebar,
  TourneyList,
} from '@/components/GenericsTournaments';
import { InscriptionSearchingResult } from '@/components/GenericsTournaments/SearchingResult';
import { ItemAutocomplete } from '@/components/InputAutocomplete';
import { DefaultTabsProps } from '@/components/Tabs';
import { TabType } from '@/components/Types';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import useToast from '@/hooks/useToast';
import useTourney from '@/hooks/useTourney';
import ROUTES from '@/router/routes';
import {
  requestAutocompleteSuggestions,
  requestTourneysExisting,
  RequestTourneysExisting,
} from '@/services/tda';
import {
  GenericItemTourneysData,
  ItemResponseTourneyExisting,
  ParticipandoTda,
  PendientesTda,
  TorneosSugeridos,
} from '@/types/tda';
import {
  cn,
  concatenateArraysWithIntervals,
  findDifferentsStringsArrays,
  GENERICS_WORDS,
  TEXTS,
} from '@/utils';

const ITEM_PER_PAGE_MOBILE = 11;
const ITEM_PER_PAGE_DESKTOP = 5;
interface ParamsRequest {
  valueSearch: string;
  page: number;
}

export const SideBarContent = React.memo(
  ({
    list,
    isLastMatchDay,
  }: {
    list: TorneosSugeridos[];
    isLastMatchDay?: boolean;
  }) => {
    const { isDesktop, isMobile } = useIsDesktop();
    const { tab, handleTabChanges } = useHandleChangesTabs();
    const navigate = useNavigate();
    const { tourneyData } = useTourney();

    const [recommended, setRecommended] = useState<
      GenericItemTourneysData[] | null
    >(null);
    const [listSuggested, setListSuggested] = useState<ItemAutocomplete[]>([]);
    const [myTourneysNames, setMyTourneysNames] = useState<string[]>([]);
    const [termSearching, setTermSearching] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [sendForm, setSendForm] = useState(false);
    const [searchResult, setSearchResult] = useState<
      GenericItemTourneysData[] | null
    >(null);
    const [cantidadBusquedas, setCantidadBusquedas] = useState(0);
    const [totalCount, setTotalCount] = useState(0);

    const tourneysPending = useMemo(() => {
      return tourneyData.tourneysPending as PendientesTda[];
    }, [tourneyData.tourneysPending]);

    const tourneysPlaying = useMemo(() => {
      return tourneyData.tourneysPlaying as ParticipandoTda[];
    }, [tourneyData.tourneysPlaying]);

    const { onHandleSubmit } = useHandleInvitations(recommended);

    const { onHandleSubmit: onHandleSubmitPostulation } = useHandleInvitations(
      searchResult,
      tab
    );

    // tabs buscador
    const tabs: DefaultTabsProps['tabs'] = useMemo(() => {
      return [
        { name: GENERICS_WORDS.tourneys },
        { name: GENERICS_WORDS.coachs },
      ];
    }, []);

    const initState = useCallback(() => {
      if (list.length > 0) {
        const formatedList = list.map((item) => ({
          id: item.idTorneoAmigos,
          title: item.nombre,
          subtitle: item.nombreDuenio,
          disabled:
            isLastMatchDay ||
            Object.values(
              tourneysPending.filter(
                (obj) => obj.id === item.idTorneoAmigos && !obj.rechazado
              )
            ).length !== 0 ||
            tourneyData.idsPendings.includes(item.idTorneoAmigos),
          secundaryText:
            (item.cantidadParticipantes || 0) +
            ' ' +
            (item.cantidadParticipantes !== 1
              ? 'participantes'
              : 'participante'),
        }));

        setRecommended(() => {
          return formatedList;
        });
      } else {
        setRecommended(() => {
          return null;
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list]);

    useEffect(() => {
      initState();

      return () => {
        setRecommended(() => {
          return null;
        });
      };
    }, [initState]);

    const {
      mutateAsync: postForm,
      isLoading,
      data,
      status,
    } = useMutation(
      async (payload: RequestTourneysExisting) =>
        await requestTourneysExisting(payload)
    );
    const toast = useToast();

    // creo array de string con torneos de los que soy dueño para comparar y no mostrarlos en resultados de autocomplete
    useEffect(() => {
      const stringArr: string[] = [];
      tourneysPlaying &&
        // eslint-disable-next-line array-callback-return
        tourneysPlaying.map((tourney) => {
          if (tourney.owner) {
            return stringArr.push(tourney.nombre);
          }
        });
      setMyTourneysNames(stringArr);
    }, [tourneysPlaying]);

    // TODO: ordenar con mayor cantidad de participantes
    const sortTournaments = (tournaments: ItemResponseTourneyExisting[]) => {
      return tournaments.sort(
        (a: ItemResponseTourneyExisting, b: ItemResponseTourneyExisting) => {
          return a.nombreTorneoAmigos.localeCompare(b.nombreTorneoAmigos);
        }
      );
    };

    const itemSearchIsPlaying = useCallback(
      (id: number): ParticipandoTda | undefined => {
        if (tourneysPlaying && tourneysPlaying.length > 0) {
          return (
            tourneysPlaying && tourneysPlaying.find((obj) => obj.id === id)
          );
        }
      },
      [tourneysPlaying]
    );

    const createListItemsTda = useCallback(
      (listResponse: ItemResponseTourneyExisting[]) => {
        const sortedTourneys = sortTournaments(listResponse);
        const list = sortedTourneys.reduce<GenericItemTourneysData[]>(
          (acc, item) => {
            const txtPlaying = itemSearchIsPlaying(item.idTorneoAmigos)
              ? 'Participando'
              : '';

            acc.push({
              id: item.idTorneoAmigos,
              action: {
                txtAction: 'Enviar solicitud',
                txtActionDisabled: txtPlaying || 'Esperando aceptación',
                variantButton: 'secondary',
              },
              title: item.nombreTorneoAmigos,
              subtitle: item.nombreUsuarioOwner,
              disabled:
                Object.values(
                  tourneysPending.filter(
                    (obj) =>
                      obj.id === item.idTorneoAmigos &&
                      !obj.rechazado &&
                      !obj.mostrarAccionPendienteAceptacion
                  )
                ).length !== 0 ||
                tourneyData.idsPendings.includes(item.idTorneoAmigos) ||
                Boolean(txtPlaying),
              secundaryText:
                item?.cantidadParticipantes &&
                `${item?.cantidadParticipantes} ${
                  item?.cantidadParticipantes !== 1
                    ? ' PARTICIPANTES'
                    : ' PARTICIPANTE'
                }`,
            });

            return acc;
          },
          []
        );

        return list;
      },
      [itemSearchIsPlaying, tourneysPending, tourneyData.idsPendings]
    );
    // seteo lista de items genericos segun servicio de busqueda
    const setGenericListTDA = useCallback(
      (list: GenericItemTourneysData[]) => {
        setSearchResult([]);
        handleTabChanges(TabType.FirstTab);
        setSearchResult(list);
      },
      [handleTabChanges]
    );

    const sortDts = (dts: ItemResponseTourneyExisting[]) => {
      // Count frequency of idUsuarioOwner
      const ownerFrequency = dts.reduce(
        (acc: any, dts: ItemResponseTourneyExisting) => {
          acc[dts.idUsuarioOwner] = (acc[dts.idUsuarioOwner] || 0) + 1;
          return acc;
        },
        {}
      );

      return dts.sort((a, b) => {
        const freqDiff =
          ownerFrequency[b.idUsuarioOwner] - ownerFrequency[a.idUsuarioOwner];
        if (freqDiff !== 0) return freqDiff;

        return a.nombreUsuarioOwner.localeCompare(b.nombreUsuarioOwner);
      });
    };

    const createListItemsDts = useCallback(
      (listResponse: ItemResponseTourneyExisting[]) => {
        // Sort the tournaments
        const sortedDts = sortDts(listResponse);
        const list = sortedDts.reduce<GenericItemTourneysData[]>(
          (acc, item) => {
            const txtPlaying = itemSearchIsPlaying(item.idTorneoAmigos)
              ? 'Participando'
              : '';

            acc.push({
              id: item.idTorneoAmigos,
              action: {
                txtAction: 'Enviar solicitud',
                txtActionDisabled: txtPlaying || 'Esperando aceptación',
                variantButton: 'secondary',
              },
              disabled:
                Object.values(
                  tourneysPending.filter(
                    (obj) =>
                      obj.id === item.idTorneoAmigos &&
                      !obj.mostrarAccionPendienteAceptacion
                  )
                ).length !== 0 ||
                tourneyData.idsPendings.includes(item.idTorneoAmigos) ||
                Boolean(txtPlaying),
              profileName:
                item.nombreUsuarioOwner + ' ' + item.apellidoUsuarioOwner,
              profilePicUrl: item?.fotoPerfil,
              title: `${item.nombreUsuarioOwner} ${item.apellidoUsuarioOwner}`,
              subtitle: item.nombreTorneoAmigos,
              secundaryText: item?.nombreProvincia
                ? item.nombreProvincia
                : undefined,
            });

            return acc;
          },
          []
        );
        return list;
      },
      [itemSearchIsPlaying, tourneyData.idsPendings, tourneysPending]
    );

    // seteo lista de items genericos segun servicio de busqueda
    const setGenericListDts = useCallback(
      (list: GenericItemTourneysData[]) => {
        setSearchResult([]);
        handleTabChanges(TabType.SecondTab);
        setSearchResult(list);
      },
      [handleTabChanges]
    );

    const setTableResult = useCallback(
      (page: number, list: ItemResponseTourneyExisting[]) => {
        if (tab === TabType.FirstTab) {
          const listItemsTda = createListItemsTda(list);
          if (page === 1) {
            setGenericListTDA(listItemsTda);
          } else {
            const newPage = listItemsTda;
            searchResult && setGenericListTDA([...searchResult, ...newPage]);
          }
        } else {
          const listItemsDts = createListItemsDts(list);
          if (page === 1) {
            setGenericListDts(listItemsDts);
          } else {
            const newPage = listItemsDts;
            searchResult && setGenericListDts([...searchResult, ...newPage]);
          }
        }
      },
      [
        createListItemsDts,
        createListItemsTda,
        searchResult,
        setGenericListDts,
        setGenericListTDA,
        tab,
      ]
    );

    // manejo de envio de consulta
    const onHandleRequest = useCallback(
      async ({ valueSearch, page }: ParamsRequest) => {
        setListSuggested([]);
        try {
          setTermSearching(valueSearch);
          const response = await postForm({
            curPage: String(page),
            perPage: isMobile
              ? String(ITEM_PER_PAGE_MOBILE)
              : String(ITEM_PER_PAGE_DESKTOP),
            name: '',
            lastname: tab === TabType.SecondTab ? valueSearch : '',
            nameTda: tab === TabType.FirstTab ? valueSearch : '',
          });

          if (response.estado) {
            setTableResult(page, response.page.objectList);
            setHasMore(response.page.hasNext);
            setCurrentPage(page);
            setCantidadBusquedas((prev) => prev + 1);
            // El total se fija con la primera página de cada búsqueda: si se
            // actualizara en cada `loadMore`, el conteo real del server puede
            // variar entre requests (datos en vivo) y el paginador movería
            // las casillas mientras el usuario está navegando esa búsqueda.
            if (page === 1) {
              setTotalCount(response.page.objectCount);
            }
          } else {
            toast.show(TEXTS.defaultError);
          }
        } catch (error) {
          console.error(TEXTS.defaultError);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [postForm, tab, toast, isMobile]
    );

    const loadMore = useCallback(
      async (pageNumber?: number) => {
        if (isLoading) return; // <-- solo bloquear si se está cargando
        const nextPage = pageNumber || currentPage + 1;

        if (termSearching) {
          await onHandleRequest({
            valueSearch: termSearching,
            page: nextPage,
          });
        }
      },
      [isLoading, currentPage, termSearching, onHandleRequest]
    );

    const handleSetGenericsByTabs = useCallback(
      (tab: TabType, data: ItemResponseTourneyExisting[]) => {
        if (tab === TabType.FirstTab) {
          setGenericListTDA(createListItemsTda(data));
        } else {
          setGenericListDts(createListItemsDts(data));
        }
      },
      [
        createListItemsDts,
        createListItemsTda,
        setGenericListDts,
        setGenericListTDA,
      ]
    );

    const handleFilterByTab = useCallback(
      async (tab: TabType) => {
        tab && handleTabChanges(tab);
        setSendForm(true);
        if (data && status) {
          handleSetGenericsByTabs(tab, data.page.objectList);
        }
      },
      [handleTabChanges, data, status, handleSetGenericsByTabs]
    );

    // request de sugerencias de autocomplete (string)
    const requestAutocomplete = useCallback(async (term: string) => {
      const searchTerm = term.toLowerCase();

      const response = await requestAutocompleteSuggestions(
        searchTerm,
        searchTerm
      );

      return response;
    }, []);

    // set list autocomplete
    const listSuggestionsAutocomplete = useCallback(
      async (term: string) => {
        setSendForm(false);

        if (term.length < 3) {
          setSearchResult(null);
          return;
        }

        const response = await requestAutocomplete(term.trim());

        const dts = response.responseTourneyDts;
        const tdas = response.responseTourneyName;

        if (dts.estado && tdas.estado) {
          const suggestions = concatenateArraysWithIntervals(
            dts.sugerencias,
            tdas.sugerencias,
            3
          );

          // creo array  de string para comparar diferencias
          const newArrSuggestions: string[] = [];
          suggestions.map((item) => {
            return newArrSuggestions.push(item.texto);
          });

          // filtro el resultado de mis torneos y otros torneos.. retorno listado sin mis torneos
          const suggestionsWithoutMe = findDifferentsStringsArrays(
            myTourneysNames,
            newArrSuggestions
          );

          const mapSuggestions = suggestionsWithoutMe.reduce<
            ItemAutocomplete[]
          >((option, name, index) => {
            option.push({
              id: index,
              value: name,
            });
            return option;
          }, []);

          setListSuggested(mapSuggestions);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [myTourneysNames]
    );

    const onHandleRefreshAfterInvitation = useCallback(
      (ids: number[], isPendings = true) => {
        const numberSet = new Set(ids);

        const disabledParticipant = {
          action: {
            txtAction: 'Enviar solicitud',
            txtActionDisabled: 'Participando',
          },
        };

        setSearchResult((prevSearchResult) => {
          if (!prevSearchResult) return null;
          return prevSearchResult.map((item) => ({
            ...item,
            disabled: (item.id && numberSet.has(item.id)) || item.disabled,
            ...(!isPendings && disabledParticipant),
          }));
        });

        setRecommended((prevRecommend) => {
          if (!prevRecommend) return null;
          return prevRecommend.map((item) => ({
            ...item,
            disabled: (item.id && numberSet.has(item.id)) || item.disabled,
            ...(!isPendings && disabledParticipant),
          }));
        });
      },
      []
    );

    useEffect(() => {
      if (tourneyData.idsPendings.length > 0) {
        onHandleRefreshAfterInvitation(tourneyData.idsPendings, true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tourneyData.idsPendings]);

    useEffect(() => {
      if (tourneyData.idsPlayings.length > 0) {
        onHandleRefreshAfterInvitation(tourneyData.idsPlayings, false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tourneyData.idsPlayings]);

    return (
      <Sidebar>
        {isDesktop && (
          <Button
            className="w-full"
            disabled={isLastMatchDay}
            type="button"
            variant="primary"
            onClick={() => {
              navigate(ROUTES.createFriendsTournament);
            }}
          >
            Crear nuevo torneo
          </Button>
        )}

        {/* Buscador torneos existentes */}
        <TourneySearch
          autocomplete={true}
          className="mt-6 lg:mt-11"
          disabled={isLastMatchDay}
          label="Inscribite a un torneo existente"
          listSuggested={listSuggested}
          listSuggestionsAutocomplete={listSuggestionsAutocomplete}
          placeholder="Nombre del torneo o administrador"
          sendForm={sendForm}
          tabSelected={tab}
          onHandleCleanResult={() => {
            setSearchResult(null);
            setCantidadBusquedas(0);
            setTotalCount(0);
          }}
          onHandleRequest={onHandleRequest}
          onHandleTabSelected={handleTabChanges}
        />

        {searchResult && (
          <div className={cn({ 'min-h-48': searchResult })}>
            <InscriptionSearchingResult
              cantidadBusquedas={cantidadBusquedas}
              hasMore={hasMore}
              list={searchResult}
              loadMore={loadMore}
              tabSelected={tab}
              tabs={tabs}
              totalCount={totalCount}
              withAvatar={tab === TabType.SecondTab}
              onHandleSubmit={onHandleSubmitPostulation}
              onSubmitTabSelected={handleFilterByTab}
            />
          </div>
        )}

        {/* Torneos Recomendados */}
        {recommended && (
          <TourneyList
            action={{
              txtAction: 'Enviar solicitud',
              txtActionDisabled: isLastMatchDay
                ? 'Enviar solicitud'
                : 'Esperando aceptación',
              variantButton: 'secondary',
            }}
            className="mt-4"
            handleSubmit={onHandleSubmit}
            list={recommended}
            title="Recomendados"
          />
        )}
      </Sidebar>
    );
  }
);

SideBarContent.displayName = 'SideBarContent';
